import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  isOnboardingProgress,
  logEvent,
  logException,
  selectAccountSettingsLoaded,
  useAuthStore,
  useEmberlee,
  useFrameAnimation,
  waitForState,
} from "@um/ui";
import { PersonaImage } from "@um/ui/components";
import { Settings } from "lucide-react";
import { CharacterConversationContainer } from "@/components/mvp/CharacterConversationContainer";
import { DentalJourneySeal } from "@/components/flows/DentalJourneySeal";
import { useCharactersStore } from "@/store/useCharactersStore";
import { useNavigate } from "react-router-dom";
import {
  EXPLORE_VARIANTS,
  getExploreVariant,
  probeExploreVariant,
  resolveExploreVariant,
  type ExploreVariantId,
} from "./exploreVariants";

/** Same Emberlee greeting sequence as the onboarding welcome step. */
function EmberleeGreetingStage() {
  const emberlee = useEmberlee();
  const greetingFrames = useMemo(
    () => emberlee.getAnimation("greeting"),
    [emberlee]
  );
  const { currentFrame } = useFrameAnimation(greetingFrames, {
    autoStart: greetingFrames.length > 0,
  });

  const displayImage =
    currentFrame?.data ??
    greetingFrames[greetingFrames.length - 1]?.data ??
    emberlee.lookup("A8");

  if (!displayImage) {
    return (
      <img
        src="/assets/emberlee.svg"
        alt="Emberlee"
        className="h-full w-full object-contain"
      />
    );
  }

  return (
    <div
      className="relative h-full mx-auto overflow-visible"
      style={{ aspectRatio: "1" }}
    >
      <PersonaImage
        image={displayImage}
        alt="Emberlee"
        className="w-full h-full"
      />
    </div>
  );
}

/**
 * Accelerated ad landing: fully static until "Let's Go". After the CTA we
 * bootstrap an anonymous account (or reuse an existing session) and continue
 * into onboarding — all driven by the click handler so it runs exactly once.
 */
export default function ExplorePage() {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startingRef = useRef(false);

  // Ad continuity: pick the copy variant that mirrors the ad that sent this
  // visitor. Google supplies the matched keyword as utm_term; Meta supplies
  // the slugified ad name as utm_content (its utm_term is the ad set id, a
  // bare number). resolveExploreVariant reads both — see exploreVariants.ts.
  const utm = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      term: params.get("utm_term"),
      campaign: params.get("utm_campaign"),
      content: params.get("utm_content"),
      resolved: resolveExploreVariant(params),
    };
  }, []);

  // Test-mode preview: overriding the variant is only offered when a test
  // resource group is selected (same gate as TestLoginButton).
  const selectedTestGroupId = useAuthStore((s) => s.selectedTestGroupId);
  const [variantOverride, setVariantOverride] =
    useState<ExploreVariantId | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Resolution probe (test mode only): type a search term, an ad name, or a
  // landing URL and see which variant a real visit would get.
  const [probeInput, setProbeInput] = useState("");
  const probe = useMemo(() => probeExploreVariant(probeInput), [probeInput]);

  const variantId = variantOverride ?? utm.resolved.id;
  const variant = getExploreVariant(variantId);
  const variantSource = variantOverride ? "override" : utm.resolved.source;

  useEffect(() => {
    // Report the variant actually rendered (previously this logged the
    // URL-derived one, so a test override was invisible in analytics), plus
    // which signal chose it — that is what makes a scent break measurable.
    logEvent("explore_page_viewed", {
      variant: variantId,
      variant_source: variantSource,
      variant_signal: utm.resolved.signal ?? undefined,
      utm_term: utm.term ?? undefined,
      utm_campaign: utm.campaign ?? undefined,
      utm_content: utm.content ?? undefined,
    });
  }, [variantId, variantSource, utm]);

  // The CTA lands on /onboarding, which is a lazy route chunk (see App.tsx).
  // Warm it while the visitor is still reading so the click isn't paying for a
  // chunk download on top of the account bootstrap it already waits on. The
  // specifier has to match App.tsx's exactly or Vite treats it as a new chunk.
  useEffect(() => {
    const prefetch = () => void import("@/pages/mvp/flows/HybridFlowPage");
    if (typeof requestIdleCallback === "function") {
      const id = requestIdleCallback(prefetch, { timeout: 3_000 });
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(prefetch, 1_500);
    return () => clearTimeout(id);
  }, []);

  const handleStart = async () => {
    // Guard against rapid double-taps (button is also disabled while starting).
    if (startingRef.current) return;
    startingRef.current = true;
    setError(null);
    setStarting(true);

    logEvent("explore_cta_clicked", {
      variant: variantId,
      variant_source: variantSource,
      utm_term: utm.term ?? undefined,
      utm_campaign: utm.campaign ?? undefined,
    });
    const startedAt = Date.now();

    try {
      await waitForState(useAuthStore, (s) => !s.initializing);

      const { userData, signInAnonymously } = useAuthStore.getState();
      if (!userData) {
        await signInAnonymously();
      }

      await waitForState(useAuthStore, (state) => {
        const accountId =
          state.selectedAccountId || state.userData?.defaultAccount?.id;
        return (
          !state.initializing &&
          !!accountId &&
          // Not `accountSettings !== undefined` — the anonymous sign-in above
          // switches accounts, and the previous account's settings satisfy
          // that check before the new account's snapshot lands, so `progress`
          // below would read as absent.
          selectAccountSettingsLoaded(state)
        );
      });

      const authState = useAuthStore.getState();
      const progress = authState.accountSettings?.onboardingProgress;
      const accountId =
        authState.selectedAccountId || authState.userData?.defaultAccount?.id;
      const updateAccountSettings = authState.updateAccountSettings;

      // ENROLMENT. Landing on the dental variant is how someone joins the
      // pilot — there is no other production path that sets this flag. It is
      // written here, after the account exists and its settings are loaded,
      // and BEFORE the returning-user shortcut below: someone who already
      // finished onboarding and then clicked a dental ad is answering that
      // ad, and every dental surface (FAB order, progress card, prompts,
      // admin roster) keys on this flag, so they should get it too.
      //
      // updateAccountSettings resolves after both the Firestore write and the
      // optimistic store patch, so by the time we navigate, HybridFlowPage's
      // seed reads `settings.dentalPilot === true` on its first render.
      if (variantId === "dental" && updateAccountSettings) {
        await updateAccountSettings({ dentalPilot: true });
        logEvent("um_dental_pilot_enrolled", {
          variant_source: variantSource,
          utm_campaign: utm.campaign ?? undefined,
          utm_content: utm.content ?? undefined,
        });
      }

      const hasCharacters = accountId
        ? await useCharactersStore.getState().checkHasCharacters(accountId)
        : false;

      // Finished onboarding → home. Use a hard navigation: client-side
      // navigation from here leaves the router's Outlet desynced (URL changes
      // but the matched route doesn't swap) because of the auth-store churn
      if (hasCharacters && !isOnboardingProgress(progress)) {
        logEvent("explore_bootstrap_completed", {
          variant: variantId,
          variant_source: variantSource,
          duration_ms: Date.now() - startedAt,
          destination: "home",
        });
        navigate("/", { replace: true });
        return;
      }

      // Skip the onboarding welcome step (this page already showed it) by
      // resuming at character-creation with isOnboarding. The "onboarding"
      // flow is only the welcome step, so treat missing progress OR progress
      // still parked on that flow as "at welcome" and reseed past it.
      const atOnboardingWelcome =
        !progress || progress.flowType === "onboarding";
      if (atOnboardingWelcome && updateAccountSettings) {
        await updateAccountSettings({
          onboardingProgress: {
            flowType: "character-creation",
            stepIndex: 0,
            collectedData: {
              ...(progress?.collectedData ?? {}),
              isOnboarding: true,
            },
            updatedAt: new Date().toISOString(),
          },
        });
      }

      logEvent("explore_bootstrap_completed", {
        variant: variantId,
        variant_source: variantSource,
        duration_ms: Date.now() - startedAt,
        destination: "onboarding",
      });
      navigate("/onboarding", { replace: true });
    } catch (err) {
      logException(err);
      logEvent("explore_bootstrap_failed", {
        variant: variantId,
        duration_ms: Date.now() - startedAt,
        message: err instanceof Error ? err.message : String(err),
      });
      console.error("[ExplorePage] Bootstrap failed:", err);
      setError("Something went wrong. Please try again.");
      setStarting(false);
      startingRef.current = false;
    }
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-gray-100">
      {selectedTestGroupId && (
        <div className="absolute right-4 top-4 z-20">
          <button
            type="button"
            aria-label="Preview page variants"
            onClick={() => setPickerOpen((open) => !open)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-600 text-white shadow-lg transition-colors hover:bg-purple-700"
          >
            <Settings className="h-5 w-5" />
          </button>
          {pickerOpen && (
            <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-purple-200 bg-white shadow-xl">
              {/* Resolution probe — check what a given search term, ad name,
                  or landing URL would actually show, without leaving the page. */}
              <div className="border-b border-purple-100 p-3">
                <label
                  htmlFor="variant-probe"
                  className="block text-xs font-semibold text-gray-700"
                >
                  What would this show?
                </label>
                <input
                  id="variant-probe"
                  type="text"
                  value={probeInput}
                  onChange={(e) => setProbeInput(e.target.value)}
                  placeholder="Search term, ad name, or landing URL"
                  autoComplete="off"
                  spellCheck={false}
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm text-gray-800 placeholder:text-gray-400 focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400"
                />

                {probeInput.trim() && (
                  <div className="mt-2 rounded-lg bg-gray-50 p-2.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-semibold text-purple-700">
                        {getExploreVariant(probe.id).label}
                      </span>
                      <span className="shrink-0 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-700">
                        {probe.source}
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] leading-snug text-gray-600">
                      {probe.note}
                    </p>
                    {probe.id !== variantId && (
                      <button
                        type="button"
                        onClick={() => {
                          setVariantOverride(probe.id);
                          setPickerOpen(false);
                        }}
                        className="mt-2 w-full rounded-md bg-purple-600 px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-purple-700"
                      >
                        Preview this
                      </button>
                    )}
                  </div>
                )}

                <p className="mt-2 text-[10px] leading-snug text-gray-400">
                  Try <code>w2-no-streaks</code>,{" "}
                  <code>habit tracker without streaks</code>, or an ad set id
                  like <code>52576648418570</code>.
                </p>
              </div>

              <div className="p-1">
                {EXPLORE_VARIANTS.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => {
                      setVariantOverride(v.id);
                      setPickerOpen(false);
                    }}
                    className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-purple-50 ${
                      v.id === variantId
                        ? "font-semibold text-purple-700"
                        : "text-gray-700"
                    }`}
                  >
                    {v.label}
                    {v.id === variantId && " ✓"}
                  </button>
                ))}
                {variantOverride && (
                  <button
                    type="button"
                    onClick={() => {
                      setVariantOverride(null);
                      setPickerOpen(false);
                    }}
                    className="mt-1 w-full rounded-lg border-t border-gray-100 px-3 py-2 text-left text-xs text-gray-500 transition-colors hover:bg-gray-50"
                  >
                    Clear override — use this visit&rsquo;s URL (
                    {utm.resolved.id})
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 pt-12 pb-4">
        <CharacterConversationContainer
          layout="full"
          characterImageSrc="/assets/emberlee.svg"
          characterImageAlt="Emberlee"
          characterImageNode={<EmberleeGreetingStage />}
          messageTitle={null}
          message={variant.message}
          actionContent={
            // `relative` so the seal below can hang off this card's bottom
            // edge; the container's card wrapper is overflow-visible and the
            // page scrolls, so the overhang is never clipped.
            <div className="relative p-4 text-gray-700">
              {/* The reset strips margins off the markdown's <p> and <ul>, so
                  without these the intro, bullets and closing line butt up
                  against each other. */}
              <div className="text-sm leading-relaxed [&_strong]:font-bold [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:my-3 [&_p+p]:mt-3">
                <ReactMarkdown>{variant.body}</ReactMarkdown>
              </div>
              {/* The pilot's rosette. It was built for the onboarding welcome
                  card, but this funnel resumes onboarding past that step, so
                  for ad traffic this page IS the welcome — the seal has to
                  live here or the ad cohort never sees it. */}
              {variantId === "dental" && <DentalJourneySeal />}
            </div>
          }
        />
      </div>

      <div className="shrink-0 border-t border-gray-200 bg-white shadow-lg">
        <div className="mx-auto max-w-lg p-4 space-y-3">
          {error && <p className="text-center text-sm text-red-600">{error}</p>}
          <button
            type="button"
            disabled={starting}
            onClick={handleStart}
            className="w-full py-3 px-6 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {starting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Getting things ready...</span>
              </>
            ) : (
              variant.cta
            )}
          </button>
          {variant.microcopy && (
            <p className="text-center text-xs text-gray-400">
              {variant.microcopy}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
